"use client"
import { useState } from "react"
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
} from "@mui/material"
import { Add, Edit, Delete, CheckCircle, Cancel } from "@mui/icons-material"
import { useApp } from "../contexts/AppContext"

export default function AppointmentManagement({ user }) {
  const { state, actions } = useApp()
  const [open, setOpen] = useState(false)
  const [selectedAppointment, setSelectedAppointment] = useState(null)
  const [formData, setFormData] = useState({
    patientName: "",
    patientId: "",
    doctorName: "",
    department: "",
    date: "",
    time: "",
    type: "",
    notes: "",
  })

  const doctors = [
    { name: "Dr. Smith", department: "Cardiology" },
    { name: "Dr. Johnson", department: "Dermatology" },
    { name: "Dr. Brown", department: "Orthopedics" },
    { name: "Dr. Davis", department: "Pediatrics" },
  ]

  const appointmentTypes = ["Consultation", "Follow-up", "Emergency", "Surgery", "Checkup"]

  const handleOpen = (appointment = null) => {
    if (appointment) {
      setSelectedAppointment(appointment)
      setFormData(appointment)
    } else {
      setSelectedAppointment(null)
      setFormData({
        patientName: "",
        patientId: "",
        doctorName: "",
        department: "",
        date: "",
        time: "",
        type: "",
        notes: "",
      })
    }
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setSelectedAppointment(null)
  }

  const handleSave = () => {
    if (selectedAppointment) {
      actions.updateAppointment({ ...formData, id: selectedAppointment.id, status: "Scheduled" })
    } else {
      actions.addAppointment({
        ...formData,
        status: "Scheduled",
        appointmentId: `A${(state.appointments.length + 1).toString().padStart(4, "0")}`,
      })
    }
    handleClose()
  }

  const handleStatusChange = (id, status) => {
    const appointment = state.appointments.find((a) => a.id === id)
    if (appointment) {
      actions.updateAppointment({ ...appointment, status })
    }
  }

  const handleDelete = (id) => {
    actions.deleteAppointment(id)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Scheduled":
        return "primary"
      case "Completed":
        return "success"
      case "Cancelled":
        return "error"
      case "No Show":
        return "warning"
      default:
        return "default"
    }
  }

  const todayAppointments = state.appointments.filter((apt) => apt.date === new Date().toISOString().split("T")[0])

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Appointment Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
          Schedule Appointment
        </Button>
      </Box>

      {/* Today's Appointments Summary */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Today's Appointments
              </Typography>
              <Typography variant="h4">{todayAppointments.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed Today
              </Typography>
              <Typography variant="h4">{todayAppointments.filter((a) => a.status === "Completed").length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Today
              </Typography>
              <Typography variant="h4">{todayAppointments.filter((a) => a.status === "Scheduled").length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Appointment ID</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Doctor</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.appointments.map((appointment) => (
              <TableRow key={appointment.id}>
                <TableCell>{appointment.appointmentId}</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{appointment.patientName}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {appointment.patientId}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{appointment.doctorName}</TableCell>
                <TableCell>{appointment.department}</TableCell>
                <TableCell>{appointment.date}</TableCell>
                <TableCell>{appointment.time}</TableCell>
                <TableCell>{appointment.type}</TableCell>
                <TableCell>
                  <Chip label={appointment.status} color={getStatusColor(appointment.status)} size="small" />
                </TableCell>
                <TableCell>
                  <Tooltip title="Complete">
                    <IconButton
                      size="small"
                      onClick={() => handleStatusChange(appointment.id, "Completed")}
                      disabled={appointment.status === "Completed"}
                    >
                      <CheckCircle />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton size="small" onClick={() => handleStatusChange(appointment.id, "Cancelled")}>
                      <Cancel />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpen(appointment)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(appointment.id)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Appointment Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{selectedAppointment ? "Edit Appointment" : "Schedule New Appointment"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Patient Name"
                value={formData.patientName}
                onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Patient ID"
                value={formData.patientId}
                onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Doctor</InputLabel>
                <Select
                  value={formData.doctorName}
                  label="Doctor"
                  onChange={(e) => {
                    const selectedDoctor = doctors.find((d) => d.name === e.target.value)
                    setFormData({
                      ...formData,
                      doctorName: e.target.value,
                      department: selectedDoctor ? selectedDoctor.department : "",
                    })
                  }}
                >
                  {doctors.map((doctor) => (
                    <MenuItem key={doctor.name} value={doctor.name}>
                      {doctor.name} - {doctor.department}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Appointment Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Appointment Type"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  {appointmentTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {selectedAppointment ? "Update" : "Schedule"} Appointment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
